import React, { ReactElement, useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Component as BoxIcon } from 'icons/icon-box--diversified.svg';
import Plan, { PlanName } from 'models/Plan';
import { BreakpointMedia } from 'config';
import AppliedCoupon from 'models/AppliedCoupon';
import { ICoupon } from 'models/Coupon';
import useSubscription from 'hooks/useSubscription';
import { formatDateToShort } from 'utils/dateTimes';
import PlanTypeHeader from '../../OrderSummary/PlanTypeHeader';

type gonData = {
  coupon?: ICoupon | null;
};

type Props = {
  preselectedPlan: Plan;
  menuShippingDate: string;
  isLoggedOut: boolean;
}

function SignupHeader({
  preselectedPlan,
  menuShippingDate,
  isLoggedOut,
}: Props): ReactElement {
  const [showOrderSummaryDrawer, setShowOrderSummaryDrawer] = useState(false);
  const [plan, setPlan] = useState(preselectedPlan);

  // if we're on Create Account step (logged out), get updated plan values from signup form elements
  const planNameFormInput = (document.getElementById('create-account-plan-name') as HTMLInputElement)?.value as PlanName;
  const extrasFormInput = Number((document.getElementById('create-account-extras') as HTMLInputElement)?.value);
  const isDiversifiedFormInput = (document.getElementById('create-account-is-diversified') as HTMLInputElement)?.value === 'true';

  const showSignupHeader = !useMediaQuery({ query: BreakpointMedia.LG });
  // @ts-expect-error TODO: fix null safety
  const appliedCouponData: ICoupon = (window.gon as gonData).coupon ?? null;
  let appliedDiscount: AppliedCoupon;

  if (isLoggedOut) {
    appliedDiscount = new AppliedCoupon({
      code: appliedCouponData?.discount_code || '',
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore -- throws an error on allowed_plans even though it is the correct type
      allowed_plans: appliedCouponData?.allowed_plans || [],
      dollar_off: appliedCouponData?.discount_type === 'dollar_off' ? appliedCouponData?.discount_price : 0,
      free_shipping: appliedCouponData?.constraints?.free_shipping || false,
      max_amount: appliedCouponData?.constraints?.max_amount || Infinity,
      max_order_count: appliedCouponData?.max_order_count || 0,
      minimum_base_price: appliedCouponData?.constraints?.minimum_base_price?.toString() || '',
      percentage_off: appliedCouponData?.discount_type === 'percentage_off' ? appliedCouponData?.discount_price : 0,
      status: '',
      extras_only: appliedCouponData?.constraints?.extras_only || false,
    });
  } else {
    /* eslint react-hooks/rules-of-hooks: 0 */
    const { subscription } = useSubscription();
    const {
      signup_summary: signupSummary,
      coupon,
    } = subscription;

    appliedDiscount = new AppliedCoupon({
      code: signupSummary?.discount?.coupon_name || '',
      allowed_plans: coupon?.allowed_plans || [],
      dollar_off: signupSummary?.discount?.coupon_discount_type === 'dollar_off' ? signupSummary?.discount?.coupon_value : 0,
      free_shipping: subscription?.coupon?.free_shipping || false,
      max_amount: signupSummary?.discount?.value || Infinity,
      max_order_count: 0,
      minimum_base_price: '',
      percentage_off: signupSummary?.discount?.coupon_discount_type === 'percentage_off' ? signupSummary?.discount?.coupon_value : 0,
      status: '',
      extras_only: false,
    });
  }

  // if we're on Create Account step (logged out), use plan values from signup form elements
  useEffect(() => {
    if (isLoggedOut) {
      const newPlan = Plan.getPlan(
        undefined,
        {
          dinner: extrasFormInput ?? 0,
          lunch: 0,
          breakfast: 0,
        },
        planNameFormInput,
        isDiversifiedFormInput,
      );

      setPlan(newPlan);
    } else {
      setPlan(preselectedPlan);
    }
  }, [isLoggedOut, planNameFormInput, extrasFormInput, isDiversifiedFormInput, preselectedPlan]);

  return (
    <>
      {showSignupHeader && (
        <>
          <div
            role="button"
            tabIndex={0}
            draggable
            className="c-signup-header-container c-sticky-widget"
            onClick={() => setShowOrderSummaryDrawer(true)}
            onDragStart={() => setShowOrderSummaryDrawer(true)}
            onKeyPress={(e) => (
              e.key === 'Enter' && setShowOrderSummaryDrawer(true)
            )}
          >
            <PlanTypeHeader
              selectedPlan={isLoggedOut ? plan : preselectedPlan}
              discount={appliedDiscount}
            />
            <div className="shipping-info">
              <span className="shipping-icon"><BoxIcon /></span>
              <span>{`Ships on ${formatDateToShort(menuShippingDate)}`}</span>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default SignupHeader;
